/*
 * @format
 */

import React from 'react';
import { Circle, ClipPath, Defs, G } from 'react-native-svg';

import WatchFace, { FACE_RADIUS } from './WatchFace';
import {
  DiagramFrame,
  HighlightBand,
  Label,
  Leader,
  stopShortOf,
  TapDot,
  TAP_RADIUS,
  TAP_SHADOW_OFFSET,
} from './DiagramParts';

/*
 * The zone screen with one of its readouts picked out as the thing to press.
 *
 * Two topics open this way — Changing modes on the mode pill, Changing
 * temperature on the setpoint — and they are the same picture annotated in the
 * same place, differing only in which row is banded. Drawn once here so they
 * cannot drift apart in size or in where the caption sits: the reader meets
 * these two screens one after another from the same menu, and a watch that
 * changed size or a caption that jumped corners between them would read as two
 * unrelated pictures.
 *
 * The frame is drawn close around the watch rather than sitting it in a wide
 * mat: the illustration fills the column it is given, so the width of the frame
 * is what decides how large the watch comes out on the phone. The caption needs
 * no width of its own — it sits below the face, not beside it — so the face
 * alone sets this.
 */
const WIDTH = 300;
const HEIGHT = 300;
const CX = WIDTH / 2;
const CY = 130;

// The band runs the width of the face, so the whole row reads as the target
// rather than just the readout's own outline.
const BAND_PADDING = 6;

/*
 * Clear of the readout's trailing edge, so what it says stays readable beside
 * it. The offset carries the fingertip's own radius as well as the gap —
 * measuring from its centre alone would lay the disc back over the readout.
 */
const TAP_GAP = 10;

/*
 * The caption sits off to one side with a line reaching the fingertip, rather
 * than centred beneath the face: centred, it reads as a title for the whole
 * picture instead of naming the one place to press.
 *
 * It is ranged right, on the same side as the tap, and the leader leaves from
 * above its text so the line never crosses the words it belongs to.
 */
const CAPTION = { x: WIDTH - 6, y: 278 };
const CAPTION_LEADER_FROM = { x: 246, y: 254 };
/*
 * The line comes up at the fingertip from below and to the right — the side its
 * shadow falls on — so it has to stop clear of the shadow rather than of the
 * disc. The clearance carries the dot on the leader's end as well as the gap
 * after it: measured to the line's stopping point alone, the dot would still
 * lie over the shadow.
 */
const LEADER_CLEARANCE = 10;

/*
 * [boxFor] gives the readout to pick out, as a box on a face centred at
 * [cx, cy] — modePillFor or setpointBoxFor. [clipId] keeps each diagram's clip
 * path to itself, since two of them could share a document.
 */
function WatchZoneTapDiagram({ boxFor, clipId, description }) {
  const box = boxFor(CX, CY);

  const band = {
    x: CX - FACE_RADIUS,
    y: box.y - BAND_PADDING,
    width: FACE_RADIUS * 2,
    height: box.height + BAND_PADDING * 2,
  };
  const tap = {
    x: box.x + box.width + TAP_RADIUS + TAP_GAP,
    y: box.y + box.height / 2,
  };
  const captionAnchor = stopShortOf(
    CAPTION_LEADER_FROM,
    tap,
    TAP_RADIUS + TAP_SHADOW_OFFSET + LEADER_CLEARANCE,
  );

  return (
    <DiagramFrame width={WIDTH} height={HEIGHT} description={description}>
      <Defs>
        <ClipPath id={clipId}>
          <Circle cx={CX} cy={CY} r={FACE_RADIUS} />
        </ClipPath>
      </Defs>

      <WatchFace cx={CX} cy={CY} />

      {/* The band is the width of the screen and cut off by its edge, as an
          overlay on the watch itself would be. */}
      <G clipPath={`url(#${clipId})`}>
        <HighlightBand {...band} />
      </G>
      <TapDot x={tap.x} y={tap.y} />

      <Leader from={CAPTION_LEADER_FROM} to={captionAnchor} />
      <Label x={CAPTION.x} y={CAPTION.y} anchor="end" bold heading="Tap here" />
    </DiagramFrame>
  );
}

export default WatchZoneTapDiagram;
